import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Footprints, Car, Bus, Maximize2, Minimize2, Wand2, Loader2, Clock, MapPin, AlertTriangle } from "lucide-react";

const DAY_COLORS = ["#1A3B2B", "#C8553D", "#4A6FA5", "#7B4B94", "#2F6D6D", "#B8860B", "#6B4E2E", "#2E7D32", "#8A3B4C", "#3B5BA5"];
const valid = (x) => x && typeof x.lat === "number" && typeof x.lng === "number";

const numberedPin = (color, n, active) =>
  L.divIcon({
    className: "wander-num-pin",
    html: `<div style="transform:translate(-14px,-14px)">
      <div style="width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #FDFBF7;box-shadow:0 1px 5px rgba(0,0,0,.35);${active ? "outline:3px solid #C8553D;outline-offset:1px;" : ""}display:flex;align-items:center;justify-content:center">
        <span style="transform:rotate(45deg);color:#FDFBF7;font-size:13px;font-weight:700;font-family:sans-serif">${n}</span>
      </div></div>`,
    iconSize: [28, 28],
  });

const hotelPin = (active) =>
  L.divIcon({
    className: "wander-hotel-pin",
    html: `<div style="width:30px;height:30px;transform:translate(-15px,-15px)">
      <div style="width:30px;height:30px;border-radius:50%;background:#B8860B;border:2px solid #FDFBF7;${active ? "outline:3px solid #C8553D;outline-offset:1px;" : ""}display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.35)">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FDFBF7" stroke-width="2"><path d="M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20"/></svg>
      </div></div>`,
    iconSize: [30, 30],
  });

function MapController({ positions, focus }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (!fitted.current && positions.length) {
      map.fitBounds(positions, { padding: [40, 40], maxZoom: 15 });
      fitted.current = true;
    }
  }, [positions, map]);
  useEffect(() => {
    if (focus) map.flyTo([focus.lat, focus.lng], Math.max(map.getZoom(), 15), { duration: 0.6 });
  }, [focus, map]);
  return null;
}

const fmtMin = (m) => (m == null ? "" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const fmtKm = (m) => (m == null ? "" : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

const PROFILES = [
  { key: "walking", label: "Walk", icon: Footprints },
  { key: "driving", label: "Drive", icon: Car },
  { key: "transit", label: "Transit", icon: Bus },
];

export const ItineraryMap = ({ trip, editable = false, onTripChange }) => {
  const itinerary = useMemo(() => trip.itinerary || [], [trip.itinerary]);
  const accoms = useMemo(() => (trip.accommodations || []).filter(valid), [trip.accommodations]);
  const [profile, setProfile] = useState("walking");
  const [routes, setRoutes] = useState({}); // dayIdx -> {legs,total_s,total_m,approx}
  const [selected, setSelected] = useState(null); // {d, i}
  const [focus, setFocus] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [optimizing, setOptimizing] = useState(null);
  const listRefs = useRef({});

  const hotelFor = useCallback(
    (city) => accoms.find((a) => a.city === city) || accoms[0] || null,
    [accoms]
  );

  // Resolve each day's ordered stops (rich activities preferred, else legacy stops).
  const days = useMemo(() => {
    return itinerary.map((d, di) => {
      const acts = (d.activities && d.activities.length ? d.activities : d.stops || []).filter(valid);
      const hotel = hotelFor(d.city);
      return { di, day: d.day, title: d.title, city: d.city, acts, hotel, color: DAY_COLORS[di % DAY_COLORS.length] };
    });
  }, [itinerary, hotelFor]);

  const allPositions = useMemo(() => {
    const pos = [];
    days.forEach((d) => {
      if (valid(d.hotel)) pos.push([d.hotel.lat, d.hotel.lng]);
      d.acts.forEach((a) => pos.push([a.lat, a.lng]));
    });
    return pos;
  }, [days]);

  // Fetch travel legs for every day whenever the profile or itinerary changes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const next = {};
      await Promise.all(
        days.map(async (d) => {
          const stops = [];
          if (valid(d.hotel)) stops.push({ lat: d.hotel.lat, lng: d.hotel.lng, name: d.hotel.name });
          d.acts.forEach((a) => stops.push({ lat: a.lat, lng: a.lng, name: a.name }));
          if (stops.length < 2) return;
          try {
            const { data } = await api.post("/trips/route", { profile, stops });
            next[d.di] = data;
          } catch {
            /* ignore per-day failure */
          }
        })
      );
      if (!cancelled) setRoutes(next);
    };
    if (days.length) run();
    return () => {
      cancelled = true;
    };
  }, [days, profile]);

  const center = useMemo(() => {
    if (valid(trip.center)) return [trip.center.lat, trip.center.lng];
    if (allPositions.length) return allPositions[0];
    return null;
  }, [trip, allPositions]);

  if (!center || allPositions.length === 0) return null;

  const selectActivity = (di, i, a) => {
    setSelected({ di, i });
    setFocus({ lat: a.lat, lng: a.lng });
  };
  const selectFromMarker = (di, i, a) => {
    setSelected({ di, i });
    setFocus({ lat: a.lat, lng: a.lng });
    const el = listRefs.current[`${di}-${i}`];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const optimizeDay = async (d) => {
    const itDay = itinerary[d.di];
    if (!itDay.activities || itDay.activities.length < 2) {
      toast.error("This day needs richer activities to optimise. Try regenerating it.");
      return;
    }
    setOptimizing(d.di);
    try {
      const firstStart = (itDay.activities.find((a) => a.start) || {}).start || "09:00";
      const { data } = await api.post("/trips/optimize-day", {
        profile,
        home: valid(d.hotel) ? { lat: d.hotel.lat, lng: d.hotel.lng } : null,
        activities: itDay.activities,
        day_start: firstStart,
      });
      const newItin = itinerary.map((x, idx) => (idx === d.di ? { ...x, activities: data.activities } : x));
      onTripChange?.({ ...trip, itinerary: newItin }, { label: `Optimise Day ${d.day}` });
      const warn = (data.warnings || []).length ? ` (${data.warnings.length} note${data.warnings.length > 1 ? "s" : ""})` : "";
      toast.success(`Day ${d.day} optimised — ≈ ${data.total_travel_min} min travelling${warn}`);
    } catch (e) {
      toast.error("Couldn't optimise this day.");
    } finally {
      setOptimizing(null);
    }
  };

  const mapEl = (
    <MapContainer center={center} zoom={trip.map_zoom || 12} scrollWheelZoom={fullscreen} style={{ height: "100%", width: "100%" }}>
      <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <MapController positions={allPositions} focus={focus} />
      {days.map((d) => {
        const line = [];
        if (valid(d.hotel)) line.push([d.hotel.lat, d.hotel.lng]);
        d.acts.forEach((a) => line.push([a.lat, a.lng]));
        return (
          <div key={`day-${d.di}`}>
            {line.length >= 2 && (
              <Polyline positions={line} pathOptions={{ color: d.color, weight: 4, opacity: 0.7, dashArray: "1 8", lineCap: "round" }} />
            )}
            {valid(d.hotel) && (
              <Marker
                position={[d.hotel.lat, d.hotel.lng]}
                icon={hotelPin(false)}
                eventHandlers={{ click: () => setFocus({ lat: d.hotel.lat, lng: d.hotel.lng }) }}
              >
                <Popup><strong>{d.hotel.name}</strong><br /><span style={{ color: "#B8860B" }}>Your stay{d.hotel.city ? ` · ${d.hotel.city}` : ""}</span></Popup>
              </Marker>
            )}
            {d.acts.map((a, i) => {
              const active = selected && selected.di === d.di && selected.i === i;
              return (
                <Marker
                  key={`m-${d.di}-${i}`}
                  position={[a.lat, a.lng]}
                  icon={numberedPin(d.color, i + 1, active)}
                  eventHandlers={{ click: () => selectFromMarker(d.di, i, a) }}
                >
                  <Popup>
                    <strong>Day {d.day} · Stop {i + 1}</strong><br />
                    {a.name}
                    {a.start ? <><br /><span style={{ color: "#C8553D" }}>{a.start}{a.duration_min ? ` · ${fmtMin(a.duration_min)}` : ""}</span></> : null}
                  </Popup>
                </Marker>
              );
            })}
          </div>
        );
      })}
    </MapContainer>
  );

  return (
    <section data-testid="itinerary-map-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h3 className="font-display text-3xl">Interactive itinerary map</h3>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-black/10 bg-white/70 p-1" data-testid="map-profile-toggle">
            {PROFILES.map((p) => (
              <button
                key={p.key}
                data-testid={`map-profile-${p.key}`}
                onClick={() => setProfile(p.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                  profile === p.key ? "bg-[#1A3B2B] text-[#FDFBF7]" : "text-[#1A3B2B] hover:bg-black/5"
                }`}
              >
                <p.icon className="w-4 h-4" /> {p.label}
              </button>
            ))}
          </div>
          <button
            data-testid="map-fullscreen-toggle"
            onClick={() => setFullscreen(true)}
            className="no-print inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-black/10 bg-white/70 text-sm hover:bg-white transition-colors"
          >
            <Maximize2 className="w-4 h-4" /> Full screen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Day / activity list */}
        <div className="lg:col-span-5 space-y-4 max-h-[520px] overflow-y-auto pr-1" data-testid="map-day-list">
          {days.map((d) => {
            const r = routes[d.di];
            const totalMin = r ? Math.round(r.duration_s / 60) : null;
            return (
              <div key={`list-${d.di}`} className="rounded-2xl border border-black/5 bg-white/60 p-5">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: d.color }} />
                    <p className="font-display text-lg">Day {d.day}{d.city ? ` · ${d.city}` : ""}</p>
                  </div>
                  {totalMin != null && (
                    <span data-testid={`day-travel-${d.day}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#F3EFE8] text-[#1A3B2B]">
                      <Clock className="w-3.5 h-3.5" /> ≈ {fmtMin(totalMin)} travel{r?.approx ? " (approx)" : ""}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">{d.title}</p>
                <ol className="space-y-1.5">
                  {d.acts.map((a, i) => {
                    const active = selected && selected.di === d.di && selected.i === i;
                    const legMin = r && r.legs && r.legs[valid(d.hotel) ? i : i - 1] ? Math.round(r.legs[valid(d.hotel) ? i : i - 1].duration_s / 60) : null;
                    return (
                      <li key={`li-${d.di}-${i}`} ref={(el) => (listRefs.current[`${d.di}-${i}`] = el)}>
                        <button
                          data-testid={`activity-${d.day}-${i + 1}`}
                          onClick={() => selectActivity(d.di, i, a)}
                          className={`w-full text-left flex gap-3 items-start p-2.5 rounded-xl transition-colors ${
                            active ? "bg-[#1A3B2B]/10 ring-1 ring-[#C8553D]" : "hover:bg-black/5"
                          }`}
                        >
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[#FDFBF7] text-xs font-bold shrink-0" style={{ background: d.color }}>{i + 1}</span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium leading-tight">{a.name}</span>
                            <span className="block text-xs text-muted-foreground mt-0.5">
                              {a.start ? `${a.start}` : ""}{a.duration_min ? ` · ${fmtMin(a.duration_min)} here` : ""}
                              {legMin != null && i > 0 ? ` · ${fmtMin(legMin)} from prev` : ""}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
                {editable && (itinerary[d.di]?.activities?.length > 1) && (
                  <button
                    data-testid={`optimize-day-${d.day}`}
                    onClick={() => optimizeDay(d)}
                    disabled={optimizing === d.di}
                    className="no-print mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1A3B2B] text-[#1A3B2B] text-xs hover:bg-[#1A3B2B] hover:text-[#FDFBF7] transition-colors disabled:opacity-50"
                  >
                    {optimizing === d.di ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Optimise route
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Map */}
        <div className="lg:col-span-7">
          <div className="relative z-0 rounded-2xl overflow-hidden border border-black/5" style={{ height: 520 }}>
            {!fullscreen && mapEl}
          </div>
          <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Tap a numbered pin or a stop in the list — they stay in sync. Times are estimates{routes && Object.values(routes).some((r) => r.approx) ? " (approx)" : ""}.
          </p>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-[1000] bg-[#0A0A0A]" data-testid="map-fullscreen">
          <div className="absolute inset-0">{mapEl}</div>
          <button
            data-testid="map-fullscreen-close"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 z-[1001] inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#1A3B2B] text-sm shadow-lg"
          >
            <Minimize2 className="w-4 h-4" /> Close
          </button>
        </div>
      )}
    </section>
  );
};
